import {useAuth} from '@apps-common/auth-react';
import {setAuthToken} from '@apps-common/js-client/api/request';
import {Box, Typography, useColorScheme} from '@mui/material';
import {useLDClient} from 'launchdarkly-react-client-sdk';
import {useGeographic as setGeographic} from 'ol/proj';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {setByFile as setGeometryByFile} from '../actions/geometry.js';
import {identify} from '../analytics.js';
import {ACCEPTED_FILE_TYPES} from '../common.js';
import Header from './Header.jsx';
import DropTarget from './drop-target.jsx';
import Hash from './hash.js';
import MapContainer from './map-container.jsx';
import Sidebar from './sidebar.jsx';

// This sets the openlayers userProjection to EPSG:4326
setGeographic();

const FILE_DRAG_TYPE = 'Files';

const App = props => {
  const [dragging, setDragging] = useState(false);
  const auth = useAuth();
  const {setMode} = useColorScheme();
  const ldClient = useLDClient();

  setAuthToken(auth.user?.accessToken || '');

  useEffect(() => {
    if (!auth.user || auth.pending || auth.error) {
      return;
    }
    identify(auth.user.claims);

    const {
      email,
      organization_id,
      organization_name,
      program_id,
      role_level,
      user_id,
    } = auth.user.claims;

    // See https://launchdarkly.github.io/js-client-sdk/interfaces/LDMultiKindContext.html
    ldClient.identify({
      kind: 'multi',
      user: {
        key: `user-${user_id}`,
        email,
        role_level,
        user_id,
        _meta: {
          privateAttributes: ['email'], //https://docs.launchdarkly.com/sdk/features/private-attributes
        },
      },
      organization: {
        key: `organization-${organization_id}`,
        organization_id,
        organization_name,
        program_id,
        _meta: {
          privateAttributes: ['organization_name'],
        },
      },
    });
  }, [auth, ldClient]);

  const onDragEnter = event => {
    const types = [...event.dataTransfer.types];
    if (types.includes(FILE_DRAG_TYPE)) {
      setDragging(true);
    }
  };

  const onDragLeave = event => {
    const types = [...event.dataTransfer.types];
    if (types.includes(FILE_DRAG_TYPE)) {
      setDragging(false);
    }
  };

  const onDrop = event => {
    event.preventDefault();
    setDragging(false);

    const types = [...event.dataTransfer.types];
    if (types.includes(FILE_DRAG_TYPE)) {
      props.setGeometryByFile(event.dataTransfer.files[0]);
    }
  };

  // Force dark mode
  useEffect(() => {
    setMode('dark');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        position: 'absolute',
        left: 0,
        top: 0,
      }}
    >
      <Header />
      <Box
        onDragEnter={onDragEnter}
        sx={{
          display: 'flex',
          position: 'relative',
          flex: 1,
        }}
      >
        <Sidebar />

        <MapContainer />

        <DropTarget open={dragging} onDrop={onDrop} onDragLeave={onDragLeave}>
          <Typography variant="h5" gutterBottom>
            Drop your file here to define area of interest
          </Typography>
          <Typography variant="h6">
            Accepted filetypes include {ACCEPTED_FILE_TYPES.join(', ')}
          </Typography>
        </DropTarget>
      </Box>
      <Hash />
    </Box>
  );
};

App.propTypes = {
  setGeometryByFile: PropTypes.func,
};

const mapDispatchProps = {
  setGeometryByFile,
};

export default connect(null, mapDispatchProps)(App);
